import {
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Collapse,
  Box,
} from "@mui/material";
import { Add as AddIcon, Close } from "@mui/icons-material";
import dayjs from "dayjs";
import { ExamTable } from "../ExamTable/ExamTable";

interface ExamResult {
  item: Array<{
    text: string;
    answer?: Array<{
      valueString?: string;
      valueDate?: string;
    }>;
  }>;
  displayInfo: {
    healthcare: string;
    practitioner: string;
    identifier: string;
  };
  answerDate: string;
}

interface ExamCardProps {
  analyte: string;
  exams: ExamResult[];
  isSelected: boolean;
  onSelect: (analyte: string | null) => void;
  onAddNew: (analyte: string) => void;
  onEdit: (index: number) => void;
  onDelete: (identifier: string) => void;
  handleOpenEditDialog: (exam: ExamResult) => void;
}

export const ExamCard = ({
  analyte,
  exams,
  isSelected,
  onSelect,
  onAddNew,
  onEdit,
  onDelete,
  handleOpenEditDialog,
}: ExamCardProps) => {
  // Filtra apenas exames com data
  const examsWithDate = exams.filter(
    (exam) => exam.item[2]?.answer?.[0]?.valueDate
  );

  // Encontra o exame mais recente comparando as datas
  const lastExam = examsWithDate.reduce((latest, current) => {
    const latestDate = dayjs(latest.item[2]?.answer?.[0]?.valueDate);
    const currentDate = dayjs(current.item[2]?.answer?.[0]?.valueDate);

    return currentDate.isAfter(latestDate) ? current : latest;
  }, examsWithDate[0]);

  const lastValue = lastExam?.item[1]?.answer?.[0]?.valueString;
  const lastDate = lastExam?.item[2]?.answer?.[0]?.valueDate;

  const unitItem = lastExam?.item?.find(
    (item) => item.text === "Unidade"
  );

  let unitValue = "";
  if (unitItem && unitItem.answer && unitItem.answer[0]?.valueString) {
    unitValue = unitItem.answer[0].valueString;
  } else {
    const valueParts = lastExam?.item[1]?.answer?.[0]?.valueString?.split(" ");
    if (valueParts && valueParts.length > 1) {
      unitValue = valueParts[1];
    }
  }

  const isRecentlyAdded = examsWithDate.some(
    (exam) => exam.displayInfo?.healthcare === "Inserção Manual"
  );

  return (
    <Tooltip
      title={isSelected ? "Clique para recolher" : "Clique para ver detalhes"}
      placement="top"
      arrow
    >
      <Card
        onClick={() => onSelect(isSelected ? null : analyte)}
        sx={{
          cursor: "pointer",
          transition: "0.3s",

          "&:hover": {
            transform: isSelected ? "none" : "translateY(-4px)",
            boxShadow: isSelected ? 4 : 3,
          },
          boxShadow: isSelected ? 4 : 1,
          border: isRecentlyAdded ? "2px solid #ed6c02" : "none",
        }}
      >
        <CardContent>
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography
                variant="h6"
                color="primary"
                sx={{
                  fontWeight: isRecentlyAdded ? 600 : 500,
                  fontSize: "1.1rem",
                }}
              >
                {analyte}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddNew(analyte);
                  }}
                  color="primary"
                  variant="outlined"
                  startIcon={<AddIcon fontSize="small" />}
                  sx={{
                    minWidth: "auto",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: "action.hover",
                      color: "primary.main",
                    },
                  }}
                >
                  Novo
                </Button>
                {isSelected && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(null);
                    }}
                    sx={{
                      color: "text.secondary",
                      "&:hover": {
                        color: "error.main",
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Stack>
            <Stack spacing={0.5}>
              <Typography
                color="text.secondary"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: "0.95rem",
                }}
              >
                <strong>Último valor:</strong> {lastValue} {unitValue && `(${unitValue})`}
              </Typography>
              {lastDate && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.9rem" }}
                >
                  <strong>Data:</strong> {dayjs(lastDate).format("DD/MM/YYYY")}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  color: "primary.main",
                  fontWeight: 500,
                  mt: 1,
                }}
              >
                {examsWithDate.length} registro
                {examsWithDate.length !== 1 ? "s" : ""}
              </Typography>
            </Stack>

            <Collapse in={isSelected} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 2 }}>
                <ExamTable
                  exams={examsWithDate}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  handleOpenEditDialog={handleOpenEditDialog}
                />
              </Box>
            </Collapse>
          </Stack>
        </CardContent>
      </Card>
    </Tooltip>
  );
};
